import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import {
    checkBluedartConsentMissing,
    fetchAppConfig,
    getConsentContent,
    getMpinStatus,
    setBluedartConsent,
    setUserInfo,
} from '../../store/actions/authActionCreator';
import {
    GetOrganisations,
    StoreOrganisation,
} from '../../store/actions/settingsActionCreator';
import { getActiveInitRoute, getLoginUser, logout } from '../../utils/helper';
import ConsentModal, { ConsentPara } from '../login/ConsentModal';
import { BD_INTERNAL_ROLES } from '../../utils/appHelper';
import { contextFromStatus, writeMpinContext } from '../../utils/mpinGate';

const CONSENT_VENDOR = 'BLUEDART';
const CONSENT_PENDING_KEY = 'CONSENT_PENDING';

interface DispatchProps {
    dispatchSetUserInfo: (data: any) => void;
    dispatchGetMpinStatus: (
        onSuccess: (resp: any) => void,
        onError: () => void,
    ) => void;
    dispatchFetchAppConfig: (
        onSuccess: (resp: any) => void,
        onError?: () => void,
    ) => void;
    dispatchGetConsentContent: (
        number: string,
        onSuccess: (resp: any) => void,
        onError: (err: any) => void,
    ) => void;
    dispatchGetOrganisations: (
        id: any,
        onSuccess: (resp: any) => void,
        onError: (resp: any) => void,
    ) => void;
    dispatchStoreOrganisation: (data: any) => void;
    dispatchSetBluedartConsent: (
        userId: any,
        onSuccess: () => void,
        onError: () => void,
    ) => void;
    dispatchCheckBluedartConsentMissing: (
        userId: any,
        onSuccess: (missing: boolean) => void,
        onError: () => void,
    ) => void;
}

type Props = DispatchProps & RouteComponentProps;

const SsoCallbackPage: React.FC<Props> = ({
    dispatchSetUserInfo,
    dispatchGetMpinStatus,
    dispatchFetchAppConfig,
    dispatchGetConsentContent,
    dispatchGetOrganisations,
    dispatchStoreOrganisation,
    dispatchSetBluedartConsent,
    dispatchCheckBluedartConsentMissing,
    history,
}) => {
    const [consentOpen, setConsentOpen] = useState(false);
    const [consentParas, setConsentParas] = useState<ConsentPara[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    // Holds the SSO token+userBO until we're ready to commit them to Redux
    // (after consent is accepted, or immediately for non-BD-vendor users).
    const pendingUserRef = useRef<{ token: string; userBO: any } | null>(null);

    const proceedToApp = () => {
        // Commit SSO user to Redux now that any consent gate is cleared.
        if (pendingUserRef.current) {
            dispatchSetUserInfo({
                status: { code: 200 },
                response: pendingUserRef.current,
            });
        }
        const orgId = getLoginUser()?.organizationId;
        dispatchGetOrganisations(
            orgId,
            (resp: any) => {
                localStorage.setItem(
                    'ORG_INFO',
                    window.btoa(
                        unescape(encodeURIComponent(JSON.stringify(resp))),
                    ),
                );
                localStorage.setItem('org', resp.name);
                dispatchStoreOrganisation(resp);
                history.push(`/${getActiveInitRoute()}`);
            },
            () => {
                history.push(`/${getActiveInitRoute()}`);
            },
        );
    };

    // MPIN gate probe — the SSO URL carries no MPIN fields, so ask the
    // server whether a step is owed before entering the app. Gated → commit
    // the pending user to redux (the /mpin greeting and later app entry
    // need state.user), arm the localStorage gate, and hand off to /mpin.
    // Probe failure with no armed gate → fail open (no evidence of cohort),
    // matching this page's consent-failure precedent.
    const gateOrProceed = () => {
        dispatchGetMpinStatus(
            (envelope: any) => {
                const mpinContext = contextFromStatus(envelope?.response);
                if (mpinContext) {
                    if (pendingUserRef.current) {
                        dispatchSetUserInfo({
                            status: { code: 200 },
                            response: pendingUserRef.current,
                        });
                    }
                    writeMpinContext(mpinContext);
                    history.replace('/mpin');
                    return;
                }
                proceedToApp();
            },
            () => proceedToApp(),
        );
    };

    const handleConsentProceed = () => {
        localStorage.removeItem(CONSENT_PENDING_KEY);
        setIsProcessing(true);
        const userId = getLoginUser()?.id;
        if (userId) {
            dispatchSetBluedartConsent(userId, gateOrProceed, gateOrProceed);
        } else {
            gateOrProceed();
        }
    };

    const handleConsentClose = () => {
        setConsentOpen(false);
        setConsentParas([]);
        setIsProcessing(false);
        logout();
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        const userEncoded = params.get('user');
        const ssoError = params.get('sso_error');

        history.replace('/sso-callback');

        if (ssoError) {
            sessionStorage.setItem('sso_error', decodeURIComponent(ssoError));
            history.replace('/login');
            return;
        }

        if (!token || !userEncoded) {
            sessionStorage.setItem(
                'sso_error',
                'Invalid SSO callback. Please try again.',
            );
            history.replace('/login');
            return;
        }

        try {
            const userBO = JSON.parse(decodeURIComponent(userEncoded));

            // Stash the user for later Redux commit, and write to localStorage now
            // so that downstream API calls (fetchAppConfig, getConsentContent) can
            // authenticate via getUserToken() without updating Redux prematurely.
            pendingUserRef.current = { token, userBO };
            localStorage.setItem(
                'USER_INFO',
                window.btoa(
                    unescape(
                        encodeURIComponent(
                            JSON.stringify({ ...userBO, token }),
                        ),
                    ),
                ),
            );

            dispatchFetchAppConfig(
                (configResp: any) => {
                    const vendorName: string =
                        configResp?.response?.configs?.vendorName ??
                        configResp?.response?.vendorName ??
                        '';
                    const role = userBO?.role ?? '0';

                    if (
                        BD_INTERNAL_ROLES?.includes(role) ||
                        String(vendorName).toUpperCase() !== CONSENT_VENDOR
                    ) {
                        gateOrProceed();
                        return;
                    }

                    const userId = getLoginUser()?.id;
                    dispatchCheckBluedartConsentMissing(
                        userId,
                        (consentMissing: boolean) => {
                            if (!consentMissing) {
                                // Consent already recorded — skip modal
                                gateOrProceed();
                                return;
                            }
                            // First time — fetch content and show modal
                            const contactNumber =
                                getLoginUser()?.contactNumber ?? '';
                            dispatchGetConsentContent(
                                contactNumber,
                                (consentResp: any) => {
                                    const paras: ConsentPara[] =
                                        consentResp?.response?.paras ?? [];
                                    if (paras.length > 0) {
                                        localStorage.setItem(
                                            CONSENT_PENDING_KEY,
                                            'true',
                                        );
                                        setConsentParas(paras);
                                        setConsentOpen(true);
                                    } else {
                                        gateOrProceed();
                                    }
                                },
                                () => gateOrProceed(),
                            );
                        },
                        () => gateOrProceed(),
                    );
                },
                () => {
                    sessionStorage.setItem(
                        'sso_error',
                        'Failed to load app config. Please try again.',
                    );
                    history.replace('/login');
                },
            );
        } catch (e) {
            sessionStorage.setItem(
                'sso_error',
                'Failed to process SSO login. Please try again.',
            );
            history.replace('/login');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <ConsentModal
                open={consentOpen}
                paras={consentParas}
                submitting={isProcessing}
                onProceed={handleConsentProceed}
                onClose={handleConsentClose}
            />
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100vh"
            >
                <CircularProgress
                    style={{ color: '#43419e', marginBottom: '16px' }}
                />
                <Typography style={{ color: '#666' }}>
                    Signing you in…
                </Typography>
            </Box>
        </>
    );
};

const mapDispatchToProps = (dispatch: any) => ({
    dispatchSetUserInfo: (data: any) => dispatch(setUserInfo(data)),

    dispatchGetMpinStatus: (
        onSuccess: (resp: any) => void,
        onError: () => void,
    ) => dispatch(getMpinStatus(onSuccess, onError)),

    dispatchFetchAppConfig: (
        onSuccess: (resp: any) => void,
        onError?: () => void,
    ) => dispatch(fetchAppConfig(onSuccess, onError)),

    dispatchGetConsentContent: (
        number: string,
        onSuccess: (resp: any) => void,
        onError: (err: any) => void,
    ) =>
        dispatch(
            getConsentContent(
                { number, source: 'desktop' },
                onSuccess,
                onError,
            ),
        ),

    dispatchGetOrganisations: (
        id: any,
        onSuccess: (resp: any) => void,
        onError: (resp: any) => void,
    ) => dispatch(GetOrganisations(id, onSuccess, onError)),

    dispatchStoreOrganisation: (data: any) => dispatch(StoreOrganisation(data)),

    dispatchSetBluedartConsent: (
        userId: any,
        onSuccess: () => void,
        onError: () => void,
    ) => dispatch(setBluedartConsent(userId, onSuccess, onError)),

    dispatchCheckBluedartConsentMissing: (
        userId: any,
        onSuccess: (missing: boolean) => void,
        onError: () => void,
    ) => dispatch(checkBluedartConsentMissing(userId, onSuccess, onError)),
});

export default withRouter(
    connect(null, mapDispatchToProps)(SsoCallbackPage as any),
);
